import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaTachometerAlt,
  FaBirthdayCake,
  FaTruck,
  FaKey,
  FaSignOutAlt,
} from 'react-icons/fa';
import './admin.css';
import { api } from '../lib/api';

const NAV_ITEMS = [
  { to: '/admin', label: 'Dashboard', end: true, Icon: FaTachometerAlt },
  { to: '/admin/cakes', label: 'Cakes', Icon: FaBirthdayCake },
  { to: '/admin/settings', label: 'Pricing & Delivery', Icon: FaTruck },
  { to: '/admin/change-password', label: 'Change Password', Icon: FaKey },
];

// Auto-logout after this many ms of no user activity
const IDLE_LIMIT_MS = 5 * 60 * 1000; // 5 minutes
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

export default function AdminLayout({ title, children }) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const idleTimerRef = useRef(null);

  // ── Session check — runs on mount, and again whenever the page is restored
  // from the browser's back-forward cache (bfcache). Without the pageshow
  // listener, pressing "back" after logout can show a stale cached dashboard
  // instead of re-validating the session with the server.
  useEffect(() => {
    let active = true;

    function checkSession() {
      api
        .me()
        .then(() => { if (active) setChecking(false); })
        .catch(() => navigate('/admin/login'));
    }

    checkSession();

    function handlePageShow(event) {
      if (event.persisted) {
        setChecking(true);
        checkSession();
      }
    }
    function handlePopState() {
      setChecking(true);
      checkSession();
    }

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', handlePopState);

    return () => {
      active = false;
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  async function handleLogout() {
    await api.logout().catch(() => {});
    navigate('/admin/login');
  }

  // Home always signs the admin out first, then sends them to the storefront —
  // leaving the dashboard is treated as ending the admin session.
  async function handleGoHome(e) {
    e.preventDefault();
    setMenuOpen(false);
    await api.logout().catch(() => {});
    navigate('/');
  }

  // ── Idle auto-logout — resets on any real user activity, fires once the
  // admin has been inactive for IDLE_LIMIT_MS. Only runs once authenticated.
  useEffect(() => {
    if (checking) return;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(async () => {
        await api.logout().catch(() => {});
        navigate('/admin/login', { state: { reason: 'idle' } });
      }, IDLE_LIMIT_MS);
    };

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [checking, navigate]);

  if (checking) {
    return (
      <div className="admin-shell">
        <div className="admin-topbar">
          <span className="admin-skel admin-skel-sidebar" style={{ width: 120, height: 16, borderRadius: 3 }} />
          <span className="admin-skel admin-skel-sidebar" style={{ width: 24, height: 20, borderRadius: 3 }} />
        </div>

        <aside className="admin-sidebar">
          <div className="admin-logo">
            <span className="admin-skel admin-skel-sidebar" style={{ width: 130, height: 22, marginBottom: 8 }} />
            <span className="admin-skel admin-skel-sidebar" style={{ width: 90, height: 10 }} />
          </div>
          <nav className="admin-nav">
            {NAV_ITEMS.map(item => (
              <span
                key={item.to}
                className="admin-skel admin-skel-sidebar"
                style={{ width: '80%', height: 14, margin: '10px 14px' }}
              />
            ))}
          </nav>
        </aside>

        <main className="admin-main">
          <span className="admin-skel admin-skel-title" />
          <div className="admin-stats">
            {[1, 2, 3, 4].map(i => (
              <div className="admin-skel-stat-card" key={i}>
                <span className="admin-skel" style={{ width: 80, height: 10, marginBottom: 12 }} />
                <span className="admin-skel" style={{ width: 40, height: 28 }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span className="admin-skel" style={{ width: 160, height: 42 }} />
            <span className="admin-skel" style={{ width: 150, height: 42 }} />
            <span className="admin-skel" style={{ width: 190, height: 42 }} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <div className="admin-topbar">
        <span>Patissora Admin</span>
        <div className="admin-topbar-actions">
          <a href="/" className="admin-topbar-home" aria-label="Log out and go to homepage" onClick={handleGoHome}>
            <FaHome />
          </a>
          <button onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
            {menuOpen ? '×' : '☰'}
          </button>
        </div>
      </div>

      <div
        className={`admin-sidebar-backdrop ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      <aside className={`admin-sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="admin-logo">
          PATISSORA
          <span>Admin Dashboard</span>
        </div>
        <nav className="admin-nav">
          <a href="/" className="admin-home-link" onClick={handleGoHome}>
            <FaHome /> Home
          </a>
          {NAV_ITEMS.map(({ to, label, end, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              <Icon /> {label}
            </NavLink>
          ))}
          <button className="admin-nav-logout" onClick={handleLogout}>
            <FaSignOutAlt /> Log Out
          </button>
        </nav>
      </aside>

      <main className="admin-main">
        {title && <h1 className="admin-title">{title}</h1>}
        {children}
      </main>
    </div>
  );
}